import React, { useCallback, useState, useRef, useEffect } from 'react';
import { FormState, RiskState, RiskImpactAssessment } from '../../types/bcpForm';
import { UserPreferences } from '../../types/userPreferences';
import InfoTooltip from './InfoTooltip';
import SaveButton from '../UI/SaveButton';

interface BusinessImpactStepProps {
  formData: FormState;
  setFormData: (data: FormState) => void;
  handleFieldChange: (field: string, value: any) => void;
  handleFieldBlur: (field: string) => void;
  shouldShowError: (field: string) => boolean;
  getFieldError: (field: string) => string;
  onSaveBIAField?: (field: string, value: any) => Promise<void>;
  risks?: RiskState;
  userPreferences?: UserPreferences | null;
}

// Custom Impact Level Dropdown Component
interface ImpactDropdownProps {
  value: 'minor' | 'moderate' | 'major' | 'catastrophic' | undefined;
  onChange: (value: 'minor' | 'moderate' | 'major' | 'catastrophic') => void;
  impactLevels: Array<{
    value: 'minor' | 'moderate' | 'major' | 'catastrophic';
    label: string;
    icon: string;
    color: string;
  }>;
}

const ImpactDropdown: React.FC<ImpactDropdownProps> = ({ value, onChange, impactLevels }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedLevel = impactLevels.find(level => level.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
          selectedLevel
            ? `${selectedLevel.color} border-current`
            : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
        }`}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center min-w-0">
            {selectedLevel ? (
              <>
                <span className="mr-1 text-xs">{selectedLevel.icon}</span>
                <span className="font-medium truncate">{selectedLevel.label}</span>
              </>
            ) : (
              <span className="text-xs text-gray-500">Select</span>
            )}
          </div>
          <svg
            className={`w-3 h-3 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white rounded-md border border-gray-300 shadow-lg">
          {impactLevels.map((level) => (
            <button
              key={level.value}
              type="button"
              onClick={() => {
                onChange(level.value);
                setIsOpen(false);
              }}
              className={`w-full px-2 py-1.5 text-left text-xs hover:bg-gray-50 transition-colors first:rounded-t-md last:rounded-b-md ${
                value === level.value ? 'bg-blue-50' : ''
              }`}
            >
              <div className="flex items-center">
                <span className="mr-1 text-xs">{level.icon}</span>
                <span className="font-medium">{level.label}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const BusinessImpactStep: React.FC<BusinessImpactStepProps> = ({
  formData,
  setFormData,
  handleFieldChange,
  handleFieldBlur,
  shouldShowError,
  getFieldError,
  onSaveBIAField,
  risks,
  userPreferences,
}) => {
  // Check if cyber assessment is enabled
  const isCyberAssessmentEnabled = userPreferences?.cyber_assessment_interest !== false;

  // Helper function to filter cyber attacks related risks
  const filterCyberAttacks = (risks: string[], includeCyber: boolean): string[] => {
    if (includeCyber) return risks;
    return risks.filter(risk =>
      !risk.toLowerCase().includes('cyber') &&
      !risk.toLowerCase().includes('cybersecurity') &&
      !risk.toLowerCase().includes('data breach') &&
      !risk.toLowerCase().includes('ransomware') &&
      !risk.toLowerCase().includes('phishing')
    );
  };

  // State for collapsible panels
  const [expandedSections, setExpandedSections] = useState({
    primaryConcerns: true,
    locationRisks: true
  });

  const toggleSection = (section: 'primaryConcerns' | 'locationRisks') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  // Simplified BIA field change handler
  const handleBIAFieldChange = useCallback((field: string, value: any) => {
    handleFieldChange(field, value);
  }, [handleFieldChange]);

  // Helper functions for risk impact assessments
  const getRiskImpact = useCallback((
    riskType: 'primary_concerns' | 'location_risks',
    riskName: string,
    impactType: keyof RiskImpactAssessment
  ): 'minor' | 'moderate' | 'major' | 'catastrophic' | undefined => {
    const assessments = formData.risk_impact_assessments?.[riskType];

    if (!assessments) {
      return undefined;
    }

    // First try the exact risk name as stored
    if (assessments[riskName]?.[impactType]) {
      return assessments[riskName][impactType];
    }

    // Try multiple variations of the risk name for backward compatibility
    const variations = [
      normalizeRiskNameForStorage(riskName), // Normalized name: "hurricanes"
      riskName.toLowerCase(), // Lowercase version: "hurricanes"
      riskName.toLowerCase().replace(/\s+/g, '_'), // Lowercase with underscores: "hurricanes"
      riskName.replace(/_/g, ' ').toLowerCase(), // "hurricanes" -> "hurricanes"
    ];

    // Remove duplicates
    const uniqueVariations = Array.from(new Set(variations));

    // Try each variation
    for (const variation of uniqueVariations) {
      if (assessments[variation]?.[impactType]) {
        return assessments[variation][impactType];
      }
    }

    // If no exact match found, try case-insensitive matching
    const storedKeys = Object.keys(assessments);
    for (const storedKey of storedKeys) {
      if (storedKey.toLowerCase() === riskName.toLowerCase()) {
        return assessments[storedKey][impactType];
      }
    }

    return undefined;
  }, [formData.risk_impact_assessments]);

  const setRiskImpact = useCallback((
    riskType: 'primary_concerns' | 'location_risks',
    riskName: string,
    impactType: keyof RiskImpactAssessment,
    value: 'minor' | 'moderate' | 'major' | 'catastrophic'
  ) => {
    const currentAssessments = formData.risk_impact_assessments || {
      primary_concerns: {},
      location_risks: {}
    };

    // Use the original risk name as the key to prevent sharing state
    const storageKey = riskName;

    const updatedAssessments = {
      ...currentAssessments,
      [riskType]: {
        ...currentAssessments[riskType],
        [storageKey]: {
          ...currentAssessments[riskType]?.[storageKey],
          [impactType]: value
        }
      }
    };

    handleFieldChange('risk_impact_assessments', updatedAssessments);
  }, [formData.risk_impact_assessments, handleFieldChange]);



  // Save business continuity objectives
  const saveContinuityObjectives = useCallback(async () => {
    if (!onSaveBIAField) return;

    await Promise.all([
      onSaveBIAField('max_downtime', formData.max_downtime),
      onSaveBIAField('recovery_priority', formData.recovery_priority),
      onSaveBIAField('rpo', formData.rpo)
    ]);
  }, [onSaveBIAField, formData]);

  // Save risk impact assessment
  const saveRiskImpactAssessment = useCallback(async () => {
    if (!onSaveBIAField) return;

    console.log('🔍 [SAVE RISK IMPACT] Saving risk impact assessments:', {
      risk_impact_assessments: formData.risk_impact_assessments,
      primary_concerns: risks?.primary_concerns || [],
      location_risks: risks?.location_risks || []
    });

    await onSaveBIAField('risk_impact_assessments', formData.risk_impact_assessments);
  }, [onSaveBIAField, formData.risk_impact_assessments, risks]);

  // Calculate financial impact based on annual revenue
  const calculateFinancialImpact = () => {
    if (formData.annual_revenue) {
      const revenue = parseAnnualRevenue(formData.annual_revenue);
      if (revenue > 0) {
        const dailyRevenue = revenue / 365;
        const hourlyRevenue = dailyRevenue / 24;
        return `Based on your annual revenue of $${formatNumber(revenue)}, your business loses approximately $${formatNumber(hourlyRevenue)} per hour of downtime.`;
      }
    }
    return '';
  };

  const parseAnnualRevenue = (revenue: string): number => {
    if (!revenue) return 0;

    // Remove currency symbols and spaces
    const cleanRevenue = revenue.replace(/[$,\s]/g, '');

    // Handle K, M, B suffixes
    const lastChar = cleanRevenue.slice(-1).toUpperCase();
    const numericValue = parseFloat(cleanRevenue.slice(0, -1));

    switch (lastChar) {
      case 'K':
        return numericValue * 1000;
      case 'M':
        return numericValue * 1000000;
      case 'B':
        return numericValue * 1000000000;
      default:
        return parseFloat(cleanRevenue) || 0;
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    } else {
      return num.toFixed(0);
    }
  };

  const impactLevels: Array<{
    value: 'minor' | 'moderate' | 'major' | 'catastrophic';
    label: string;
    description: string;
    color: string;
    icon: string;
  }> = [
    { value: 'minor', label: 'Low', description: 'Minimal impact on operations', color: 'bg-green-100 text-green-800 border-green-300', icon: '🟢' },
    { value: 'moderate', label: 'Medium', description: 'Moderate impact on operations', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: '🟡' },
    { value: 'major', label: 'High', description: 'Significant impact on operations', color: 'bg-orange-100 text-orange-800 border-orange-300', icon: '🟠' },
    { value: 'catastrophic', label: 'Critical', description: 'Severe impact on operations', color: 'bg-red-100 text-red-800 border-red-300', icon: '🔴' }
  ];

  // Helper function to format risk names
  const formatRiskName = (riskName: string): string => {
    return riskName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to normalize risk names for storage (reverse of formatRiskName)
  const normalizeRiskNameForStorage = (riskName: string): string => {
    // Convert back to underscore format for consistent storage
    return riskName
      .toLowerCase()
      .replace(/\s+/g, '_');
  };

  // Helper function to get risk description
  const getRiskDescription = (riskName: string): string => {
    const descriptions: { [key: string]: string } = {
      'cyber_attack': 'Malicious attempts to damage or disrupt computer systems',
      'natural_disaster': 'Events like earthquakes, floods, hurricanes, or wildfires',
      'supply_chain_disruption': 'Interruptions in the flow of goods and services',
      'economic_downturn': 'Periods of economic decline affecting business operations',
      'loss_of_key_personnel': 'Departure of critical employees or management',
      'pandemic': 'Widespread disease affecting workforce and operations',
      'power_outage': 'Loss of electrical power affecting business operations',
      'fire': 'Fire incidents that could damage facilities or equipment',
      'flooding': 'Water damage from floods or water system failures',
      'earthquake': 'Seismic activity that could damage infrastructure',
      'wildfires': 'Uncontrolled fires that could threaten facilities',
      'extreme_weather': 'Severe weather conditions affecting operations'
    };
    return descriptions[riskName.toLowerCase()] || 'Potential business disruption risk';
  };

  // Helper functions for risk categorization based on Risk Impact Assessment Matrix
  const getRiskLevel = useCallback((riskName: string, riskType: 'primary_concerns' | 'location_risks'): 'high' | 'medium' | 'low' => {
    const assessment = formData.risk_impact_assessments?.[riskType]?.[riskName];
    if (!assessment) return 'low';

    // Get all impact assessments from the matrix
    const impacts = [
      assessment.financial_impact,
      assessment.operational_impact,
      assessment.reputational_impact,
      assessment.regulatory_impact
    ];

    // Count impact levels
    const catastrophicCount = impacts.filter(impact => impact === 'catastrophic').length;
    const majorCount = impacts.filter(impact => impact === 'major').length;
    const moderateCount = impacts.filter(impact => impact === 'moderate').length;

    // Risk categorization logic:
    // High: Any catastrophic impact OR 2+ major impacts
    if (catastrophicCount > 0 || majorCount >= 2) return 'high';

    // Medium: 1 major impact OR 2+ moderate impacts OR any combination of major + moderate
    if (majorCount === 1 || moderateCount >= 2 || (majorCount > 0 && moderateCount > 0)) return 'medium';

    // Low: Only minor impacts or 1 moderate impact
    return 'low';
  }, [formData.risk_impact_assessments]);

  const getRiskCountByLevel = useCallback((level: 'high' | 'medium' | 'low'): number => {
    if (!risks) return 0;

    const allRisks = [
      ...filterCyberAttacks(risks.primary_concerns, isCyberAssessmentEnabled).map(risk => ({ name: risk, category: 'Primary Concerns' })),
      ...filterCyberAttacks(risks.location_risks, isCyberAssessmentEnabled).map(risk => ({ name: risk, category: 'Location Risks' }))
    ];

    return allRisks.filter(risk => {
      const riskType = risk.category === 'Primary Concerns' ? 'primary_concerns' : 'location_risks';
      return getRiskLevel(risk.name, riskType) === level;
    }).length;
  }, [risks, isCyberAssessmentEnabled, getRiskLevel]);

  const getRisksByLevel = useCallback((level: 'high' | 'medium' | 'low') => {
    if (!risks) return [];

    const allRisks = [
      ...filterCyberAttacks(risks.primary_concerns, isCyberAssessmentEnabled).map(risk => ({ name: risk, category: 'Primary Concerns' })),
      ...filterCyberAttacks(risks.location_risks, isCyberAssessmentEnabled).map(risk => ({ name: risk, category: 'Location Risks' }))
    ];

    return allRisks.filter(risk => {
      const riskType = risk.category === 'Primary Concerns' ? 'primary_concerns' : 'location_risks';
      return getRiskLevel(risk.name, riskType) === level;
    });
  }, [risks, isCyberAssessmentEnabled, getRiskLevel]);

  // Simplified component for BIA fields with save buttons

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          Business Impact Analysis
          <InfoTooltip content="The process of identifying an organization's critical business functions, estimating the potential impact of a disruption on those functions (financial, operational, legal, and reputational), and determining the recovery timeframes and resource requirements needed to restore operations to an acceptable level." />
        </h2>
        <p className="text-gray-600">
          Assess the potential impact of business disruptions on your organization
        </p>
      </div>

      {/* Financial Impact Analysis */}
      <div className="p-6 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Financial Impact Analysis</h3>
          <InfoTooltip content="Financial impact analysis helps determine the cost of downtime and guides recovery time objectives (RTO)." />
        </div>

        {calculateFinancialImpact() && (
          <div className="p-4 mb-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-900">Hourly Downtime Cost</p>
                <p className="text-sm text-blue-700">{calculateFinancialImpact()}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Maximum Tolerable Downtime (MTD)
            </label>
            <select
              value={formData.max_downtime || ''}
              onChange={(e) => handleBIAFieldChange('max_downtime', e.target.value)}
              onBlur={() => handleFieldBlur('max_downtime')}
              className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select MTD</option>
              <option value="0-1 hour">0-1 hour</option>
              <option value="1-4 hours">1-4 hours</option>
              <option value="4-24 hours">4-24 hours</option>
              <option value="1-3 days">1-3 days</option>
              <option value="3-7 days">3-7 days</option>
              <option value="More than 1 week">More than 1 week</option>
            </select>
            {shouldShowError('max_downtime') && (
              <p className="mt-1 text-xs text-red-500">{getFieldError('max_downtime')}</p>
            )}
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Recovery Priority
            </label>
            <select
              value={formData.recovery_priority || ''}
              onChange={(e) => handleBIAFieldChange('recovery_priority', e.target.value)}
              onBlur={() => handleFieldBlur('recovery_priority')}
              className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Priority</option>
              <option value="0-1 hour">Immediate (0-1 hour)</option>
              <option value="1-4 hours">Urgent (1-4 hours)</option>
              <option value="4-24 hours">High (4-24 hours)</option>
              <option value="1-3 days">Medium (1-3 days)</option>
              <option value="3+ days">Low (3+ days)</option>
            </select>
            {shouldShowError('recovery_priority') && (
              <p className="mt-1 text-xs text-red-500">{getFieldError('recovery_priority')}</p>
            )}
          </div>
        </div>

        {/* Save button for Financial Impact Analysis */}
        <div className="flex justify-end mt-6">
          <SaveButton
            onSave={saveContinuityObjectives}
            successMessage="Financial impact settings saved successfully!"
            errorMessage="Failed to save financial impact settings. Please try again."
            size="md"
            variant="primary"
          >
            Save Financial Impact
          </SaveButton>
        </div>
      </div>


      {/* Risk-Specific Impact Assessment Matrix */}
      {risks && (filterCyberAttacks(risks.primary_concerns, isCyberAssessmentEnabled).length > 0 || filterCyberAttacks(risks.location_risks, isCyberAssessmentEnabled).length > 0) && (
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="mb-6">
            <h3 className="mb-2 text-xl font-bold text-gray-900">
              Risk Impact Assessment Matrix
              <InfoTooltip content="The evaluation of the potential consequences (financial, operational, legal, regulatory, and reputational) that a risk event could have on an organization's ability to continue critical operations, used to prioritize risks within the business continuity planning process." />
            </h3>
            <p className="text-sm text-gray-600">
            Assess the potential impact of each identified risk across different business areas
            </p>
          </div>

          <div className="space-y-8">
            {/* Primary Concerns */}
            {filterCyberAttacks(risks.primary_concerns, isCyberAssessmentEnabled).length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                              <button
                  onClick={() => toggleSection('primaryConcerns')}
                  className="px-6 py-4 w-full text-left transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                >
                  <h4 className="flex justify-between items-center text-lg font-semibold text-gray-800">
                    <div className="flex items-center">
                      <span className="mr-3 w-2 h-2 bg-blue-500 rounded-full"></span>
                      Primary Business Concerns
                      <span className="px-2 py-1 ml-3 text-xs text-blue-700 bg-blue-100 rounded-full">
                        {filterCyberAttacks(risks.primary_concerns, isCyberAssessmentEnabled).length} risks
                      </span>
                          </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                        expandedSections.primaryConcerns ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </h4>
                              </button>

                {expandedSections.primaryConcerns && (
                  <div className="px-4 pb-4">
                    <div className="overflow-x-auto">
                      <table className="w-full bg-white rounded-lg shadow-sm border-collapse min-w-[600px]">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-200 w-[180px]">
                              Risk
                            </th>
                            <th className="px-2 py-2 text-xs font-semibold text-center text-gray-700 border-b border-gray-200 w-[100px]">
                              Financial
                              <InfoTooltip content="Rate the potential financial impact of business disruption" />
                            </th>
                            <th className="px-2 py-2 text-xs font-semibold text-center text-gray-700 border-b border-gray-200 w-[100px]">
                              Operational
                              <InfoTooltip content="Rate the potential operational impact of business disruption" />
                            </th>
                            <th className="px-2 py-2 text-xs font-semibold text-center text-gray-700 border-b border-gray-200 w-[100px]">
                              Reputational
                              <InfoTooltip content="Rate the potential reputational impact of business disruption" />
                            </th>
                            <th className="px-2 py-2 text-xs font-semibold text-center text-gray-700 border-b border-gray-200 w-[100px]">
                              Regulatory
                              <InfoTooltip content="Rate the potential legal/regulatory impact of business disruption" />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filterCyberAttacks(risks.primary_concerns, isCyberAssessmentEnabled).map((risk, index) => (
                            <tr key={`primary-${index}`} className="transition-colors hover:bg-gray-50">
                              <td className="px-3 py-3 border-b border-gray-100">
                                <div>
                                  <div className="mb-1 text-sm font-medium text-gray-900">
                                    {formatRiskName(risk)}
                          </div>
                                  <div className="text-xs leading-tight text-gray-500">
                                    {getRiskDescription(risk)}
                          </div>
                        </div>
                              </td>
                              {['financial_impact', 'operational_impact', 'reputational_impact', 'regulatory_impact'].map((impactType) => {
                                const currentValue = getRiskImpact('primary_concerns', risk, impactType as keyof RiskImpactAssessment);

                                return (
                                  <td key={impactType} className="px-2 py-3 text-center border-b border-gray-100">
                                    <ImpactDropdown
                                      value={currentValue}
                                      onChange={(value) => setRiskImpact('primary_concerns', risk, impactType as keyof RiskImpactAssessment, value)}
                                      impactLevels={impactLevels}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                </div>
                )}
              </div>
            )}

            {/* Location Risks */}
            {filterCyberAttacks(risks.location_risks, isCyberAssessmentEnabled).length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                              <button
                  onClick={() => toggleSection('locationRisks')}
                  className="px-6 py-4 w-full text-left transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-inset"
                >
                  <h4 className="flex justify-between items-center text-lg font-semibold text-gray-800">
                    <div className="flex items-center">
                      <span className="mr-3 w-2 h-2 bg-green-500 rounded-full"></span>
                      Location-Specific Risks
                      <span className="px-2 py-1 ml-3 text-xs text-green-700 bg-green-100 rounded-full">
                        {filterCyberAttacks(risks.location_risks, isCyberAssessmentEnabled).length} risks
                      </span>
                          </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                        expandedSections.locationRisks ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </h4>
                              </button>

                {expandedSections.locationRisks && (
                  <div className="px-4 pb-4">
                    <div className="overflow-x-auto">
                      <table className="w-full bg-white rounded-lg shadow-sm border-collapse min-w-[600px]">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-200 w-[180px]">
                              Risk
                            </th>
                            <th className="px-2 py-2 text-xs font-semibold text-center text-gray-700 border-b border-gray-200 w-[100px]">
                              Financial
                              <InfoTooltip content="Rate the potential financial impact of business disruption" />
                            </th>
                            <th className="px-2 py-2 text-xs font-semibold text-center text-gray-700 border-b border-gray-200 w-[100px]">
                              Operational
                              <InfoTooltip content="Rate the potential operational impact of business disruption" />
                            </th>
                            <th className="px-2 py-2 text-xs font-semibold text-center text-gray-700 border-b border-gray-200 w-[100px]">
                              Reputational
                              <InfoTooltip content="Rate the potential reputational impact of business disruption" />
                            </th>
                            <th className="px-2 py-2 text-xs font-semibold text-center text-gray-700 border-b border-gray-200 w-[100px]">
                              Regulatory
                              <InfoTooltip content="Rate the potential legal/regulatory impact of business disruption" />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filterCyberAttacks(risks.location_risks, isCyberAssessmentEnabled).map((risk, index) => (
                            <tr key={`location-${index}`} className="transition-colors hover:bg-gray-50">
                              <td className="px-3 py-3 border-b border-gray-100">
                                <div>
                                  <div className="mb-1 text-sm font-medium text-gray-900">
                                    {formatRiskName(risk)}
                          </div>
                                  <div className="text-xs leading-tight text-gray-500">
                                    {getRiskDescription(risk)}
                          </div>
                        </div>
                              </td>
                              {['financial_impact', 'operational_impact', 'reputational_impact', 'regulatory_impact'].map((impactType) => {
                                const currentValue = getRiskImpact('location_risks', risk, impactType as keyof RiskImpactAssessment);

                                return (
                                  <td key={impactType} className="px-2 py-3 text-center border-b border-gray-100">
                                    <ImpactDropdown
                                      value={currentValue}
                                      onChange={(value) => setRiskImpact('location_risks', risk, impactType as keyof RiskImpactAssessment, value)}
                                      impactLevels={impactLevels}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                </div>
                )}
              </div>
            )}
          </div>

          {/* Save button for Risk Impact Assessment Matrix */}
          <div className="flex justify-end pt-6 mt-8 border-t border-gray-200">
            <SaveButton
              onSave={saveRiskImpactAssessment}
              successMessage="Risk impact assessment saved successfully!"
              errorMessage="Failed to save risk impact assessment. Please try again."
              size="md"
              variant="primary"
              className="bg-green-600 hover:bg-green-700"
            >
              Save Risk Impact Assessment
            </SaveButton>
          </div>
        </div>
      )}

      {/* Risk Summary */}
      <div className="p-6 bg-white rounded-lg border border-gray-200">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          Risk Summary
          <InfoTooltip content="Summary of risks from the Risk Impact Assessment Matrix above, categorized by overall impact level based on financial, operational, reputational, and regulatory assessments." />
        </h3>
        <p className="mb-4 text-sm text-gray-600">
          Risk categorization based on the Risk Impact Assessment Matrix completed above
        </p>

        {risks && (filterCyberAttacks(risks.primary_concerns, isCyberAssessmentEnabled).length > 0 || filterCyberAttacks(risks.location_risks, isCyberAssessmentEnabled).length > 0) ? (
          <div className="space-y-6">
            {/* Risk Summary Cards */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* High Risk */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center mb-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                  <h4 className="text-sm font-semibold text-red-900">High Risk</h4>
                </div>
                <div className="text-2xl font-bold text-red-700 mb-1">
                  {getRiskCountByLevel('high')}
                </div>
                <div className="text-xs text-red-600">
                  {getRiskCountByLevel('high') === 1 ? 'risk' : 'risks'} identified
                </div>
              </div>

              {/* Medium Risk */}
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center mb-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                  <h4 className="text-sm font-semibold text-yellow-900">Medium Risk</h4>
                </div>
                <div className="text-2xl font-bold text-yellow-700 mb-1">
                  {getRiskCountByLevel('medium')}
                </div>
                <div className="text-xs text-yellow-600">
                  {getRiskCountByLevel('medium') === 1 ? 'risk' : 'risks'} identified
                </div>
              </div>

              {/* Low Risk */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center mb-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <h4 className="text-sm font-semibold text-green-900">Low Risk</h4>
                </div>
                <div className="text-2xl font-bold text-green-700 mb-1">
                  {getRiskCountByLevel('low')}
                </div>
                <div className="text-xs text-green-600">
                  {getRiskCountByLevel('low') === 1 ? 'risk' : 'risks'} identified
                </div>
              </div>
            </div>

            {/* Detailed Risk Breakdown */}
            <div className="space-y-4">
              {/* High Risk Details */}
              {getRisksByLevel('high').length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h5 className="text-sm font-semibold text-red-900 mb-2">High Risk Items</h5>
                  <div className="space-y-1">
                    {getRisksByLevel('high').map((risk, index) => (
                      <div key={index} className="text-sm text-red-700">
                        • {risk.name} ({risk.category})
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Medium Risk Details */}
              {getRisksByLevel('medium').length > 0 && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h5 className="text-sm font-semibold text-yellow-900 mb-2">Medium Risk Items</h5>
                  <div className="space-y-1">
                    {getRisksByLevel('medium').map((risk, index) => (
                      <div key={index} className="text-sm text-yellow-700">
                        • {risk.name} ({risk.category})
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Low Risk Details */}
              {getRisksByLevel('low').length > 0 && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h5 className="text-sm font-semibold text-green-900 mb-2">Low Risk Items</h5>
                  <div className="space-y-1">
                    {getRisksByLevel('low').map((risk, index) => (
                      <div key={index} className="text-sm text-green-700">
                        • {risk.name} ({risk.category})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Complete the Risk Impact Assessment Matrix above to see your risk summary
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Risks are categorized as High (catastrophic impacts or 2+ major impacts), Medium (1 major or 2+ moderate impacts), or Low (minor impacts only)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessImpactStep;
